{
  "general": {
    "title": "General Information",
    "description": "Manage your organization's key details, including its name, entity name, and support links. Configure help center visibility and provide a support URL for client assistance.",
    "organizationName": "Organization Name",
    "entityName": "Entity Name",
    "supportUrl": "Support URL",
    "viewHelpCenter": "View Help Center",
    "helpCenterUrl": "Help Center URL",
    "parent": "Parent Organization",
    "subdomain": "Subdomain",
    "id": "ID",
    "enableContactSupport": "Enable Contact Support",
    "enableResourcesAndArticles": "Enable Resources and Articles",
    "helpCentreUrl": "Help Centre URL",
    "urlError": "Invalid URL"
  },
  "designer": {
    "title": "Designer",
    "description": "Customize the platform's visual identity by updating logos, favicon links, and browser tab titles. Choose whether to inherit styling from the parent organization or apply unique branding elements.",
    "faviconLink": "Favicon link",
    "faviconLinkHelpText": "The favicon link is the URL of the favicon image. The favicon is the small icon that appears in the browser tab.",
    "browserTabTitle": "Browser tab title",
    "example": "Example",
    "exampleTitle": "Example Title",
    "exampleDescription": "This is what the design will look like",
    "topBarColor": "Top Bar Color",
    "topBarTextColor": "Top Bar Text Color",
    "primaryColor": "Primary Color",
    "buttonBorderRadius": "Button Border Radius",
    "pageBackgroundColor": "Page Background Color",
    "borderRadiusRound": "Round",
    "borderRadiusLarge": "Large",
    "borderRadiusMedium": "Medium",
    "borderRadiusNone": "None",
    "borderRadiusSmall": "Small",
    "cardBorderRadius": "Card Border Radius",
    "logo": "Logo",
    "moreChanges": "If you want to make more in depth changes to the design of your platform, please contact your administrator.",
    "useParentThemeTokens": "Inherit styling from parent organization",
    "colorError": "Invalid hex code color"
  },
  "authenticationDesigner": {
    "title": "Authentication Designer",
    "description": "Customize the login page by setting a logo, primary color, and background color to align with your organization's branding.",
    "logoLink": "Logo URL (Authentication flow)",
    "logoLinkHelpText": "This is a link to the logo image to show on the login page.",
    "primaryColor": "Primary color (Authentication flow)",
    "pageBackgroundColor": "Page background color (Authentication flow)"
  },
  "errorPageDesigner": {
    "title": "Error Page",
    "description": "Customize the error page by defining the message, description, and button label. Adjust the background color and choose whether to display the navigation bar.",
    "errorPageMessage": "Error message",
    "errorPageDescription": "Error description",
    "errorPageButtonText": "Button text",
    "errorPageBackgroundColor": "Background Color",
    "errorPageShowNavbar": "Show Navigation Bar"
  },
  "textDesigner": {
    "title": "Text Variants",
    "loading": "Loading text designer settings…",
    "loadError": "Failed to load text designer settings",
    "description": "",
    "textVariants": "Text Variants",
    "changeOrgLevelText": "Customize typography (font, size, weight, color) for headings, body text, and labels. Changes apply across your organization and are visible to clients.",
    "fontSize": "Font Size",
    "fontSizeHelp": "e.g., 16px, 1.5rem",
    "fontFamily": "Font Family",
    "fontFamilyHelp": "e.g., Inter, Arial, sans-serif",
    "color": "Color",
    "fontWeight": "Font Weight",
    "fontWeightHelp": "e.g., 400, 600, 700",
    "letterSpacing": "Letter Spacing",
    "letterSpacingHelp": "e.g., 0px, -0.25px",
    "lineHeight": "Line Height",
    "lineHeightHelp": "e.g., 24px, 1.5",
    "apply": "Apply",
    "applyConfirmTitle": "Apply text variant changes?",
    "applyConfirmMessage": "These changes will be immediately visible to clients. Typography will update across the application after you confirm.",
    "validationFontWeight": "Font weight must be a number between 1 and 1000 (e.g., 400, 700).",
    "validationLetterSpacing": "Use a length (e.g., 0px, -0.25px, 0.1em) or the word \"normal\".",
    "validationLineHeight": "Use a number (e.g., 1.5), a length (e.g., 24px), or the word \"normal\".",
    "validationColor": "Enter a valid hex color (e.g., #000000 or #fff).",
    "validationFontSize": "Use a length with unit (e.g., 16px, 1.5rem, 42px)."
  },
  "accessUsers": {
    "title": "Access Users",
    "description": "Add and manage users with platform access. Assign roles, define access levels, and manage invites."
  },
  "authentication": {
    "title": "Authentication",
    "description": "Configure authentication settings, including login methods, custom redirect URLs, and multi-factor authentication options to enhance security.",
    "customUrl": "Custom URL",
    "customUrlHelpText": "Before updating this field, make sure the CNAME has been set by the CloudOps team",
    "enableSingleLogout": "Enable Single Logout",
    "logoutRedirectUrl": "Logout Redirect URL",
    "logoutRedirectUrlHelpText": "Before updating this field, make sure it has been updated in auth0",
    "defaultAuthenticationConnection": "Default Authentication",
    "enableMultiFactorAuthentication": "Enable Multi-Factor Authentication",
    "defaultAuthenticationConnectionOptions": {
      "EMAIL_AND_PASSWORD": "Email and Password",
      "EMAIL_OTP": "Email - One-time code (Passwordless)",
      "ENTERPRISE": "Enterprise SSO"
    },
    "multiFactorAuthenticationTitle": "Multi-Factor Authentication Options",
    "multiFactorAuthenticationOptions": {
      "EMAIL": "Email",
      "SMS": "SMS",
      "AUTHENTICATOR": "Authenticator"
    },
    "termsAndConditionsUrl": "Terms and Conditions URL",
    "auth0DisplayName": "Display name",
    "enableLoginSettings": "Enable Login Settings",
    "loginSettingsTitle": "Title",
    "loginUrl": "Login URL"
  },
  "roles": {
    "title": "Roles",
    "description": "Manage and configure roles within the platform. Assign role profiles, define permissions, and customize navigation styles to align with user responsibilities and access needs."
  },
  "roleProfiles": {
    "title": "Role Profiles",
    "description": "Create and manage role profiles to define user permissions and access levels. Configure navigation styles, page access, and specific permissions to align with organizational needs.",
    "useParentRoleProfiles": "Inherit role profiles from parent organization"
  },
  "accessSettings": {
    "title": "Access Settings",
    "description": "Define user access rules, including permissions for sub-organizations, logout options, automatic user invitations, and inactivity timeouts for enhanced security.",
    "allowAccessToSubOrganizations": "Allow access to sub-organizations",
    "allowLogout": "Allow logout",
    "autoInviteImportedUsers": "Auto-invite imported users",
    "enableInactivityTimeOut": "Enable Inactivity Timeout",
    "inactivityTimeoutInMinutes": "Inactivity Timeout (minutes)",
    "enableEmailFrenchTranslation": "Enable Email French Translation"
  },
  "dashboards": {
    "title": "Dashboards",
    "description": "Create and manage dashboards to display key performance indicators, metrics, and data visualizations. Customize dashboard layouts, widgets, and data sources to align with organizational goals and reporting requirements."
  },
  "notificationGroups": {
    "title": "Notification Groups",
    "description": "Create and manage notification groups to define how users receive alerts. Configure in-app, push, email, and webhook notifications, and set default states for user preferences.",
    "useParentNotificationGroups": "Inherit notification groups from parent organization"
  },
  "notificationDefinitions": {
    "title": "Notification Definitions",
    "description": "Define specific notification events and configure message templates. Assign notifications to groups, set trigger events, and customize delivery methods, including in-app, email, and webhook notifications.",
    "useParentNotificationDefinitions": "Inherit notification definitions from parent organization"
  },
  "forms": {
    "title": "Forms",
    "description": "Create and manage forms for signing agreements, collecting user information, and managing client data.",
    "useParentFormTemplates": "Inherit form templates from parent organization"
  },
  "localization": {
    "title": "Localization",
    "description": "Configure region-specific settings, including enabled jurisdictions, tax identification formats, and currency display preferences to ensure compliance and consistency across markets.",
    "displayCurrency": "Display currency throughout platform",
    "localization": "Localization",
    "noLocalization": "none (defaults apply)",
    "country": "country",
    "currency": "currency",
    "productShelfCurrencies": "Product Shelf Currencies",
    "baseCurrency": "Base Currency"
  },
  "notifications": {
    "groups": "Groups",
    "definitions": "Definitions"
  },
  "statementsAndTaxDocuments": {
    "title": "Statements and Tax Documents",
    "description": "Configure access to client reports, agreements, and financial documents. Enable or disable OneVest and custodian statements, agreements, and tax documents based on organizational preferences."
  },
  "repCodes": {
    "title": "Rep Codes",
    "description": "Create and manage rep codes.",
    "useParentRepCodes": "Inherit rep codes from parent organization"
  },
  "pageConfigurations": {
    "title": "Page Configurations",
    "description": "Manage and customize page layouts to align with your organization's needs. Create new configurations, define types, and control whether to inherit settings from the parent organization.",
    "useParentPageConfigurations": "Inherit page configurations from parent organization",
    "type": "Type",
    "pageConfigurationTypes": {
      "INDIVIDUAL": "Individual",
      "NON_INDIVIDUAL": "Non-Individual",
      "ACCOUNT": "Account",
      "GOAL": "Goal",
      "HOUSEHOLD": "Household",
      "SUB_ACCOUNT": "Sub Account",
      "ACCOUNT_GROUP": "Account Group"
    }
  },
  "homePageConfigurations": {
    "title": "Home Page Configurations",
    "description": "Manage and customize home page layouts to align with your organization's needs. Create new configurations, define widgets, and control whether to inherit settings from the parent organization.",
    "useParentHomePageConfigurations": "Inherit home page configurations from parent organization",
    "table": {
      "name": "Name",
      "widgetCount": "Widget Count",
      "organization": "Organization",
      "createdAt": "Created At"
    }
  },
  "staticPageWidgetConfigurations": {
    "title": "Static Page Widget Configurations",
    "description": "Manage and customize static page widget layout to align with your organization's needs. Create new configurations, define types, and control whether to inherit settings from the parent organization.",
    "useParentStaticPageWidgetConfigurations": "Inherit static page widget configurations from parent organization",
    "model": "Static Page Widget Configuration"
  },
  "workflows": {
    "title": "Workflows",
    "description": "Create, manage, and customize workflows to automate key processes. Define workflow states, triggers, and step sequences to streamline client onboarding, approvals, and other operational tasks.",
    "useParentWorkflows": "Inherit workflows from parent organization",
    "states": "States",
    "workflowStates": {
      "DRAFT": "Draft",
      "ACTIVE": "Active",
      "ARCHIVED": "Archived"
    }
  },
  "customFields": {
    "title": "Custom Fields",
    "description": "Create and manage custom fields to capture additional data specific to your organization's needs. Define field types, associated objects, and apply conditional rules to enhance data structure and usability.",
    "useParentCustomFields": "Inherit custom fields from parent organization"
  },
  "clientReports": {
    "title": "Client Reports",
    "description": "Configure client reports to display client-specific information, including account balances, transactions, and performance metrics.",
    "useParentClientReports": "Inherit client reports from parent organization"
  },
  "downtimeSchedule": {
    "title": "Downtime Schedule",
    "description": "Create and manage scheduled downtimes for platform maintenance. Define affected platforms, start and end times, and specify role-based exclusions to control user impact."
  },
  "newsAndInsights": {
    "title": "News and Insights",
    "description": "Manage and publish articles to keep users informed. Choose to inherit content from the parent organization or upload custom insights relevant to your audience.",
    "useParentArticles": "Inherit articles from parent organization"
  },
  "tradingSchedules": {
    "title": "Trading Schedules",
    "description": "Manage and configure trading schedules to align with investment strategies. View existing schedules, update dates, and create new schedules to control trade execution timing.",
    "useParentSchedules": "Inherit trading schedules from parent organization"
  },
  "assetClasses": {
    "title": "Asset Classes",
    "description": "Manage and customize asset classes used in portfolio construction. View, edit, and add classifications to align with investment strategies. Choose to inherit asset classes from the parent organization.",
    "useParentAssetClasses": "Inherit asset classes from parent organization"
  },
  "feeGrids": {
    "title": "Fee Grids",
    "description": "Define and manage fee structures, including tiered and blended models. Configure billable AUM calculations and default fee settings.",
    "useParentFeeGrids": "Inherit fee grids from parent organization",
    "configureTitle": "Configure Fee Grid",
    "createTitle": "Create Fee Grid",
    "createBeforeAssignTitle": "Create a fee grid first",
    "createBeforeAssignBody": "Once saved, you'll be able to assign entities to it.",
    "settingsSectionTitle": "Settings",
    "noEntities": "No {{entity}} are using this fee grid.",
    "entityPlural": {
      "subAccount": "sub accounts",
      "account": "accounts",
      "accountGroup": "account groups",
      "user": "users",
      "clientGroup": "client groups"
    },
    "defaultFallbackBanner": "This is the default fee grid. It applies to all entities as a fallback even without an explicit assignment, so some entities using it may not appear in this list.",
    "inheritedParentBanner": "This fee grid is inherited from a parent organization. Entities assigned to it at the parent level are not visible here.",
    "assign": "Assign",
    "assigned": "Assigned",
    "assignedToOtherGrid": "Assigned to another grid",
    "assignEntityModalTitle": "Assign entity",
    "assignSearchPlaceholder": "Search by name",
    "assignSearchByIdPlaceholder": "Search by ID",
    "assignNoResults": "No matching entities found.",
    "assignArchivedTooltip": "Archived or inactive entities can't be assigned.",
    "assignSuccess": "Fee grid assigned successfully.",
    "assignError": "Failed to assign fee grid.",
    "unassign": "Unassign",
    "unassignSuccess": "Fee grid unassigned successfully.",
    "unassignError": "Failed to unassign fee grid.",
    "tabs": {
      "subAccount": "Sub Account",
      "account": "Account",
      "accountGroup": "Account Group",
      "user": "User",
      "clientGroup": "Client Group"
    }
  },
  "billingSchedules": {
    "title": "Billing Schedules",
    "description": "Set up billing schedules with calculation and billing dates. Define default schedules and configure fee processing rules.",
    "useParentBillingSchedules": "Inherit billing schedules from parent organization",
    "configureTitle": "Configure Billing Schedule",
    "createTitle": "Create Billing Schedule",
    "createBeforeAssignTitle": "Create a billing schedule first",
    "createBeforeAssignBody": "Once saved, you'll be able to assign entities to it.",
    "settingsSectionTitle": "Settings",
    "noEntities": "No {{entity}} are using this billing schedule.",
    "entityPlural": {
      "subAccount": "sub accounts",
      "account": "accounts",
      "accountGroup": "account groups",
      "user": "users",
      "clientGroup": "client groups"
    },
    "defaultFallbackBanner": "This is the default billing schedule. It applies to all entities as a fallback even without an explicit assignment, so some entities using it may not appear in this list.",
    "inheritedParentBanner": "This billing schedule is inherited from a parent organization. Entities assigned to it at the parent level are not visible here.",
    "assign": "Assign",
    "assigned": "Assigned",
    "assignedToOtherSchedule": "Assigned to another schedule",
    "assignEntityModalTitle": "Assign entity",
    "assignSearchPlaceholder": "Search by name",
    "assignSearchByIdPlaceholder": "Search by ID",
    "assignNoResults": "No matching entities found.",
    "assignArchivedTooltip": "Archived or inactive entities can't be assigned.",
    "assignSuccess": "Billing schedule assigned successfully.",
    "assignError": "Failed to assign billing schedule.",
    "unassign": "Unassign",
    "unassignSuccess": "Billing schedule unassigned successfully.",
    "unassignError": "Failed to unassign billing schedule.",
    "tabs": {
      "subAccount": "Sub Account",
      "account": "Account",
      "accountGroup": "Account Group",
      "user": "User",
      "clientGroup": "Client Group"
    }
  },
  "integrations": {
    "title": "Integrations",
    "description": "Manage and add third-party connections to the platform. Users can view, inherit, or create new integrations by selecting a type (e.g., banking, analytics, ID verification). These integrations help streamline data sharing and enhance platform functionality.",
    "useParentIntegrations": "Inherit integrations from parent organization"
  },
  "localizations": {
    "title": "Localizations",
    "description": "Configure region-specific settings, including enabled jurisdictions, tax identification formats, and currency display preferences to ensure compliance and consistency across markets.",
    "useParentLocalizations": "Inherit localizations from parent organization"
  },
  "apiTokens": {
    "title": "API Tokens",
    "description": "Create and manage secure access keys for system integrations. Users can define permissions (Read, Write, Transition) for various entities like clients, roles, and financial products, ensuring controlled data access."
  },
  "webhooks": {
    "title": "Webhooks",
    "description": "Webhooks enable real-time event notifications to external systems. Users can configure triggers, specify destinations, and customize payloads to automate workflows and keep data synchronized across platforms.",
    "useParentWebhooks": "Inherit webhooks from parent organization"
  },
  "bulkImport": {
    "title": "Bulk Import",
    "description": "Initiate new data imports and monitor ongoing or completed import jobs. It provides insights into processing status, including queued, failed, and successfully imported records."
  },
  "transfers": {
    "title": "Transfers",
    "description": "Configure transfer settings, including transaction review requirements, fraud prevention, and minimum deposit thresholds. Set cash holding periods before funds can be withdrawn or traded.",
    "cashOnHoldToWithdrawDays": "Number of days to hold cash before it can be withdrawn",
    "cashOnHoldToTradeDays": "Number of days to hold cash before it can be traded",
    "cancelTransferCutoffDays": "Number of days before a pending scheduled transfer where a scheduled transfer cannot be cancelled",
    "reviewTransactions": "Review Transactions",
    "blockSuspiciousTransactions": "Block Suspicious Transactions",
    "minInitialDepositCents": "Minimum Initial Deposit",
    "minRecurringDepositCents": "Minimum Scheduled Deposit (one-time, recurring)",
    "minDepositCents": "Minimum Deposit",
    "enableFullCashTransfers": "Enable full cash transfers",
    "enablePartialCashTransfers": "Enable partial cash transfers",
    "enableAllInKindTransfers": "Enable all in kind transfers",
    "minInitialDepositCentsError": "Minimum initial deposit must be greater than 1",
    "minRecurringDepositCentsError": "Minimum scheduled deposit must be greater than 1",
    "minDepositCentsError": "Minimum deposit must be greater than 1"
  },
  "trades": {
    "title": "Trades",
    "description": "Configure trade execution settings, including auto-investments, post-optimization trade editing, and default trading processes.",
    "allowPostOptimizationEditing": "Allow editing on trades after optimization",
    "defaultTradingProcess": "Default Trading Process",
    "optimizerProcess": "Optimizer Engine",
    "optimizerProcessTooltip": "Applies only to portfolios using the Portfolio optimizer trading process.",
    "requireSubTradeRequestApproval": "Trade verification required",
    "allowViewSubTradeRequestPairs": "Allow view sub-trade request pairs",
    "bulkTradingProcess": "Default bulk trading process",
    "defaultDriftTolerancePercent": "Default drift tolerance (%)",
    "driftRangeHint": "Enter a value between 5-20%",
    "driftRangeError": "Value is invalid and must be between 5-20%",
    "driftRecalculationInProgress": "Drift recalculation in progress. The tolerance setting and Save are temporarily disabled while portfolios are re-evaluated.",
    "driftRecalculationComplete": "Drift recalculation complete.",
    "driftConfirmTitle": "Apply new drift tolerance?",
    "driftConfirmBody": "Changing the default drift tolerance will trigger a recalculation across every portfolio in your organization. This may take several minutes to complete.",
    "driftConfirmApply": "Apply and recalculate"
  },
  "compliance": {
    "title": "Compliance",
    "description": "Define compliance settings to ensure regulatory adherence. Configure account approval requirements, ID verification, annual information updates, and restrictions on inactive users and transactions.",
    "allowInactiveUserRebalancing": "Allow inactive users to be rebalanced",
    "allowInactiveUserFeeCalculation": "Allow inactive users to have fees calculated",
    "allowInactiveUserAdjustments": "Allow inactive users to have adjustments made",
    "allowInstantInvest": "Allow instant investment",
    "requireManualAccountApproval": "Require manual account approval",
    "requireSubTradeRequestApproval": "Require sub-trade request approval",
    "profileReviewTimeInMonths": "Profile review time (months)"
  },
  "flagPolicies": {
    "title": "Flag Policies",
    "description": "Configure reconciliation flag behavior for this organization. View effective policies including inherited and platform defaults, then override enabled state, priority, and amount thresholds per flag type.",
    "noAccess": "You do not have permission to view flag policy settings.",
    "searchPlaceholder": "Search flag types",
    "noResults": "No flag types match your search.",
    "futureFlagsNotice": "Changes to flag policy criticality will only apply to future flags. Existing flags will not be affected.",
    "config": {
      "priority": "Priority",
      "thresholdDollars": "Threshold ($)",
      "thresholdCents": "Threshold (cents)",
      "thresholdPercent": "Threshold (%)"
    },
    "priorityChangeConfirm": {
      "title": "Change flag priority?",
      "body": "Updating the criticality of this rebalance flag will change how it is surfaced and actioned across the platform. Please confirm this is intentional."
    },
    "validation": {
      "duplicatePolicy": "Flag policies contain duplicate flag types.",
      "duplicateThreshold": "Flag thresholds contain duplicate flag types.",
      "policyRowIncomplete": "Each flag policy override must set enabled and/or priority.",
      "invalidPriority": "Priority must be Critical, High, Medium, or Low.",
      "thresholdRowIncomplete": "Each threshold override must set threshold cents and/or percent threshold.",
      "negativeThresholdCents": "Threshold must be zero or greater.",
      "invalidPercentThreshold": "Percent threshold must be between 0 and 100.",
      "percentOnlyNoCents": "This flag type only supports a percent threshold."
    }
  },
  "portfolioManagementSettings": {
    "title": "Portfolio Management Settings",
    "description": "Configure portfolio management preferences, including inheritance of product shelves, model portfolios, and themes. Enable or disable features such as pending transactions, tax loss harvesting, and fix trading to align with investment strategies.",
    "useParentProductShelf": "Inherit product shelf from parent organization",
    "useParentModelPortfolios": "Inherit model portfolios from parent organization",
    "useParentThemes": "Inherit themes from parent organization",
    "allowPortfolioPerGoal": "Allow portfolio set onto the goal",
    "allowPendingTransactions": "Allow pending transactions",
    "disableStatsUpdateBasedOnTransactions": "Disable stats update based on transactions",
    "useHistoricalSnapshotsCutOffDate": "Enable historical data cut-off date",
    "historicalSnapshotsCutOffDate": "Historical data cut-off date",
    "historicalSnapshotsCutOffDateInfo": "Transactions dated after this cut-off will be included in the positions calculation. Transactions before this date will be excluded. Baseline positions must be provided at the cut-off date to establish the initial state (cash, holdings, and cost basis).",
    "disableRebalanceReport": "Disable rebalance report calculation",
    "hideAutoUpdatePrices": "Hide auto update prices",
    "enableManualUpdateExchangeRates": "Enable manual update exchange rates",
    "enableAutoUpdateExchangeRates": "Enable auto update exchange rates",
    "marketValueDipThresholdPercentage": "Market value dip flag threshold (%)",
    "marketValueDipThresholdPercentageInfo": "Day-over-day market value drop that triggers a flag during reconciliation. Minimum 5%, defaults to 15% if not set.",
    "marketValueDipThresholdPercentageMinError": "Must be at least 5%"
  },
  "feeSettings": {
    "title": "General",
    "description": "Configure fee-related settings, including the ability to charge administrative fees to clients and require fee approval.",
    "enableFees": "Enable Fees",
    "chargeAdminFeesToClient": "Charge admin fees to client",
    "allowChargePST": "Allow Charge PST",
    "requireFeeApproval": "Require fee approval"
  },
  "embedded": {
    "title": "Embedded",
    "description": "Configure how the platform functions when embedded in another application. Users can set a JWT Key URL for secure authentication and choose whether to display the menu bar when embedded.",
    "jwtKeyUrl": "JWT Key URL",
    "displayMenuBar": "Display menu bar when embedded"
  },
  "roleSettings": {
    "title": "Role Settings",
    "description": "Set the default roles for users when they sign up, including the default sign-up role, default advisor role, and default client role. This allows for customization of user roles based on organizational requirements.",
    "defaultSignUpRole": "Default sign up role",
    "defaultAdvisorRole": "Default advisor role",
    "defaultClientRole": "Default client role"
  },
  "accounts": {
    "title": "Accounts",
    "description": "Enable or disable account types available within the platform, including but not limited to individual, corporate, and registered accounts. Configure permissions for different account structures based on organizational requirements."
  },
  "accountSettings": {
    "title": "Account Settings",
    "description": "Configure account related settings, including settings related to affiliate operations."
  },
  "accountGroupSettings": {
    "title": "Account Group Settings",
    "description": "Configure organization-level settings for account groups.",
    "enableAutoArchive": "Enable automatic account group archiving",
    "stopDateCustomField": {
      "label": "Stop date custom field",
      "placeholder": "None",
      "helperText": "Select the date custom field on account groups that determines when a group should be automatically archived."
    }
  },
  "taxRankings": {
    "title": "Tax Rankings",
    "description": "Set the tax rankings for the accounts the are available in the platform.",
    "useParentTaxRanks": "Use parent tax rankings"
  },
  "generalProductExperience": {
    "title": "General",
    "description": "Configure essential settings for your product experience, including default behaviors, and general preferences."
  },
  "formDefinitions": {
    "title": "Form Definitions",
    "description": "Manage form definitions and their configurations for your organization",
    "definitions": "Definitions",
    "signerTypes": "Signer Types",
    "useParentFormDefinitions": "Inherit form definitions from parent organization",
    "table": {
      "key": "Key",
      "name": "Name",
      "type": "Type",
      "objectType": "Object Type",
      "state": "State",
      "actions": "Actions"
    },
    "state": {
      "DRAFT": "Draft",
      "ACTIVE": "Active",
      "ARCHIVED": "Archived"
    },
    "objectType": {
      "ACCOUNT": "Account",
      "SUB_ACCOUNT": "Sub Account",
      "ENTITY": "Entity",
      "GOAL": "Goal",
      "HOUSEHOLD": "Household"
    },
    "formDefinitionModal": {
      "title": "New Form Definition",
      "editTitle": "Edit Form Definition",
      "key": "Key",
      "name": "Name",
      "description": "Description",
      "pdfFileNameTemplate": "PDF File Name",
      "pdfFileNameTemplateHelper": "Handlebars template for the download filename (e.g. {{user.lastName}}_{{user.firstName}}_NCAF)",
      "objectType": "Object Type",
      "type": "Type",
      "ENTITY": "Entity",
      "SUB_ACCOUNT": "Sub Account",
      "ACCOUNT": "Account",
      "GOAL": "Goal",
      "HOUSEHOLD": "Household",
      "PDF_IN_APP": "PDF - In App Signing",
      "NONE_UPLOAD": "Upload Only",
      "PDF_UPLOAD": "PDF - Upload",
      "PDF_INTEGRATION": "PDF - Integration",
      "fillingType": "Filling Type",
      "FILLING_TYPE_NONE": "None",
      "FILLING_TYPE_FORM_MAP": "Form Map",
      "FILLING_TYPE_WEBHOOK": "Webhook",
      "FILLING_TYPE_STATIC": "Static",
      "formMaps": "Form Maps",
      "noFormMaps": "No form maps configured for this definition.",
      "formMapRow": "Form Map created at {{date}} in {{language}}",
      "addFormMap": "Add Form Map",
      "template": "Form Template",
      "basicTemplate": "Uploaded Form",
      "externalTemplate": "Externally Filled Form",
      "url": "External URL",
      "apiToken": "API Token",
      "signerKey": "Signer Key",
      "pageNumber": "Page #",
      "x": "X Position",
      "y": "Y Position"
    },
    "signers": "Signers"
  },
  "signerTypes": {
    "title": "Signer Types",
    "description": "Manage signer types and their configurations for your organization",
    "useParentSignerTypes": "Inherit signer types from parent organization",
    "table": {
      "key": "Key",
      "name": "Name",
      "isPrimary": "Is Primary",
      "defaultSigningOrder": "Default Signing Order",
      "primary": "Primary",
      "secondary": "Secondary"
    },
    "signerTypeModal": {
      "title": "New Signer Type",
      "editTitle": "Edit Signer Type",
      "key": "Key",
      "name": "Name",
      "isPrimary": "Is Primary",
      "defaultSigningOrder": "Default Signing Order"
    },
    "editSignerTypeModal": {
      "title": "Edit Signer Type",
      "key": "Key",
      "name": "Name",
      "isPrimary": "Is Primary",
      "defaultSigningOrder": "Default Signing Order"
    }
  },
  "custodianConnections": {
    "title": "Custodian Connections",
    "description": "Manage custodian connections for your organization. Configure connections to custodians for trading, account management, and data synchronization.",
    "modelName": "custodian connection",
    "useParentCustodianConnections": "Inherit custodian connections from parent organization",
    "table": {
      "name": "Name",
      "type": "Type",
      "default": "Default",
      "organization": "Organization",
      "actions": "Actions",
      "inherited": "Inherited",
      "yes": "Yes"
    },
    "types": {
      "APEX": "Apex",
      "ATB": "ATB",
      "CIBC_MELLON": "CIBC Mellon",
      "CIIS": "CIIS",
      "ENVESTNET": "Envestnet",
      "FC": "FC",
      "FUNDSERV": "Fundserv",
      "IBKR": "IBKR",
      "NBIN": "NBIN",
      "NO_CONNECTION": "No Connection",
      "PERSHING": "Pershing",
      "RBC": "RBC",
      "SCHWAB": "Schwab"
    },
    "modal": {
      "createTitle": "New Custodian Connection",
      "editTitle": "Edit Custodian Connection",
      "name": "Name",
      "type": "Type",
      "customerName": "Customer Name",
      "repCode": "Rep Code",
      "default": "Default connection",
      "updateSuccess": "Custodian connection updated successfully"
    },
    "detail": {
      "breadcrumb": "Custodian Connection",
      "description": "Configure this {{type}} custodian connection",
      "notFound": "Custodian connection not found",
      "saveSuccess": "Custodian connection updated successfully",
      "tabs": {
        "basicInfo": "Basic Info",
        "accountTypeSettings": "Account Types",
        "sftpCredentials": "SFTP Credentials",
        "config": "Configuration",
        "featureFlags": "Feature Flags"
      },
      "basicInfo": {
        "title": "General",
        "description": "Core identification and connection settings"
      },
      "submissionSettings": {
        "title": "Submission & Reconciliation",
        "description": "Configure how trades, transfers, and account openings are submitted and reconciled"
      },
      "reconciliationType": "Reconciliation Type",
      "reconciliationTypes": {
        "FULL": "Full",
        "HYBRID": "Hybrid",
        "READ_ONLY": "Read Only"
      },
      "transferSubmissionType": "Transfer Submission Type",
      "transferSubmissionTypes": {
        "BATCH": "Batch",
        "EVENT": "Event"
      },
      "accountOpeningSubmissionType": "Account Opening Submission Type",
      "accountOpeningTypes": {
        "BATCH": "Batch",
        "EVENT": "Event"
      },
      "notificationEmails": "Notification Emails",
      "notificationEmailsHelper": "Comma-separated list of email addresses",
      "accountTypes": {
        "title": "Account Type Settings",
        "addAccountType": "Add Account Type",
        "removeAccountType": "Remove",
        "newAccountType": "New Account Type",
        "empty": "No account type settings configured. Add one to define currency and affiliate settings per account type.",
        "accountType": "Account Type",
        "baseCurrency": "Base Currency",
        "availableCurrencies": "Available Currencies",
        "enableMultiCurrency": "Enable Multi-Currency",
        "enableAutoSweep": "Enable Auto Sweep",
        "multiCurrency": "Multi-Currency",
        "affiliateSettings": "Affiliate Settings",
        "addAffiliate": "Add Affiliate",
        "affiliateType": "Affiliate Type",
        "affiliateDescription": "Description",
        "requiredFields": "Required Fields",
        "optionalFields": "Optional Fields"
      },
      "sftp": {
        "title": "SFTP Credentials",
        "inbound": "Inbound (SFTP In)",
        "inboundDescription": "Credentials for downloading files from the custodian",
        "outbound": "Outbound (SFTP Out)",
        "outboundDescription": "Credentials for uploading files to the custodian",
        "authenticationType": "Authentication Type",
        "authTypes": {
          "USERNAME_PASSWORD": "Username & Password",
          "PRIVATE_KEY": "Private Key"
        },
        "host": "Host",
        "port": "Port",
        "username": "Username",
        "password": "Password",
        "privateKey": "Private Key",
        "passphrase": "Passphrase",
        "sourceDir": "Source Directory",
        "targetPrefix": "Target Prefix",
        "targetDir": "Target Directory",
        "bucket": "S3 Bucket",
        "bucketVariableName": "Bucket Variable Name",
        "serverHostKey": "Server Host Key Types"
      },
      "config": {
        "title": "Connection Configuration",
        "description": "Generic JSON configuration for this custodian connection. This is custodian-specific and varies by integration type.",
        "invalidJson": "Invalid JSON format"
      },
      "flags": {
        "title": "Feature Flags",
        "groups": {
          "import": "Data Import",
          "reconciliation": "Reconciliation",
          "submission": "Submission",
          "transfers": "Deposit Transfers",
          "fetching": "Data Fetching",
          "trading": "Trading & Accounts",
          "filePaths": "Import File Paths"
        },
        "enableCustodianActivityImport": "Activity Import",
        "enableCustodianSnapshotImport": "Snapshot Import",
        "enableCustodianAccountImport": "Account Import",
        "enableCustodianUserImport": "User Import",
        "enableCustodianOrganizationImport": "Organization Import",
        "enableCustodianOrganizationUserImport": "Organization User Import",
        "enableCustodianCredentialsManager": "Credentials Manager",
        "enableAutoReconcileActivities": "Auto Reconcile Activities",
        "enableAutoReconcilePositions": "Auto Reconcile Positions",
        "enableTransferSubmission": "Transfer Submission",
        "enableAccountOpeningSubmission": "Account Opening Submission",
        "enableCancelCustodianDepositTransfer": "Cancel Deposit Transfer",
        "enableChallengedCustodianDepositTransfer": "Challenged Deposit Transfer",
        "enableCreateCustodianDepositTransfers": "Create Deposit Transfers",
        "enableSaveInternalRecordsOfCustodianDepositTransfers": "Save Internal Deposit Transfer Records",
        "enableFetchCustodianStatistics": "Fetch Statistics",
        "enableFetchCustodianSuitability": "Fetch Suitability",
        "enableFetchCustodianSnapshotHistory": "Fetch Snapshot History",
        "enableFetchCustodianTransactions": "Fetch Transactions",
        "enableFetchCustodianAffiliates": "Fetch Affiliates",
        "enableFetchCustodianBankAccounts": "Fetch Bank Accounts",
        "enableFetchCustodianUpcomingTransactions": "Fetch Upcoming Transactions",
        "enableFetchCustodianStatements": "Fetch Statements",
        "enableFetchCustodianCustomFields": "Fetch Custom Fields",
        "enableFetchCustodianProjectedIncome": "Fetch Projected Income",
        "enableFetchCustodianNotifications": "Fetch Notifications",
        "allowCashAsDepositTarget": "Allow Cash as Deposit Target",
        "allowFractionalTrading": "Allow Fractional Trading",
        "allowTransactionCreation": "Allow Transaction Creation",
        "disableCorpInventoryAccount": "Disable Corp Inventory Account",
        "fetchLivePrices": "Fetch Live Prices",
        "custodianActivityImportFilePaths": "Activity Import File Paths",
        "custodianSnapshotImportFilePaths": "Snapshot Import File Paths",
        "custodianUsersImportFilePath": "Users Import File Path",
        "filePathsHelper": "Comma-separated list of file paths"
      }
    }
  },
  "featureFlags": {
    "title": "Feature Flags",
    "description": "Manage feature flags to control platform features and behaviors. Enable or disable specific features based on organizational requirements."
  },
  "bulkTradingProcessOptions": {
    "pair": "PAIR",
    "bucket": "BUCKET"
  },
  "customAttributeLabels": {
    "title": "Custom Attribute Labels",
    "editTitle": "Edit Custom Attribute Label",
    "description": "Create & manage custom attribute labels for your organization",
    "objectType": "Object Type",
    "name": "Attribute Name",
    "options": {
      "label": "Label",
      "options": "Options",
      "description": "Description",
      "value": "Value"
    },
    "useParentCustomAttributeLabels": "Inherit custom attribute labels from parent organization",
    "attributeNames": {
      "RISK_QUESTION_1": "Risk Question 1",
      "RISK_QUESTION_2": "Risk Question 2",
      "GOAL_RISK_QUESTION_1": "Goal Risk Question 1",
      "TIME_HORIZON": "Time Horizon",
      "INVESTMENT_KNOWLEDGE": "Investment Knowledge"
    },
    "buttons": {
      "add": "Add"
    },
    "states": {
      "ACTIVE": "Active",
      "ARCHIVED": "Archived",
      "INACTIVE": "Inactive"
    },
    "objectTypes": {
      "GOAL": "Goal",
      "USER": "User"
    },
    "table": {
      "attribute": "Attribute Name",
      "objectType": "Object Type",
      "state": "State",
      "organization": "Organization",
      "actions": "Actions"
    },
    "actions": {
      "activate": "Activate",
      "archive": "Archive",
      "deactivate": "Deactivate"
    },
    "validation": {
      "optionsRequired": "At least one option is required"
    }
  },
  "alertBanners": {
    "title": "Alert Banner",
    "description": "Alert Banners appear at the top of the screen, just below the header, and are intended for timely, high-value messages such as scheduled maintenance notices or new feature announcements. To maintain a clean and focused experience, it’s best to keep the number of active banners to a minimum. Use the date range to control when a banner is shown."
  },
  "workflowUpload": {
    "button": "Upload",
    "success": "Workflow created successfully!",
    "error": "Failed to create workflow",
    "validationError": "Validation Error",
    "validationMessage": "The uploaded JSON file has the following issues:",
    "invalidJsonFile": "Invalid JSON file. Please check the file format.",
    "missingName": "Missing required field: name",
    "missingSteps": "Missing required field: steps",
    "stepsMustBeArray": "Field \"steps\" must be an array",
    "workflowMustHaveStep": "Workflow must have at least one step",
    "stepMissingName": "Step {{index}}: Missing required field \"name\"",
    "stepMissingSubSteps": "Step {{index}}: Missing required field \"subSteps\"",
    "stepSubStepsMustBeArray": "Step {{index}}: Field \"subSteps\" must be an array",
    "subStepMissingType": "Step {{stepIndex}}, SubStep {{subStepIndex}}: Missing required field \"type\"",
    "usedIn": "Used in: {{title}}",
    "uploadError": "Upload Error",
    "confirmTitle": "Upload Workflow",
    "confirmMessage": "Are you sure you want to create a new workflow from this file?",
    "create": "Create Workflow",
    "fileName": "File",
    "workflowName": "Workflow Name",
    "steps": "Steps",
    "trigger": "Trigger",
    "mapRoleProfiles": "Map Role Profiles",
    "roleMappingMessage": "The uploaded workflow contains role profiles. Please map them to existing role profiles in your organization:",
    "mapTo": "Map to existing role profile",
    "selectRoleProfile": "Select role profiles",
    "noRoleProfilesTitle": "No role profiles available to map",
    "noRoleProfilesMessage": "The uploaded workflow contains role profiles, but there are no role profiles available in your organization to map them to.",
    "noRoleProfilesHint": "You can either create the required role profiles first, or choose to unlink all role profiles to continue.",
    "requiredRoleProfiles": "Role profiles required by this workflow:",
    "unlinkAllRoleProfiles": "Unlink all role profiles (set all rolesCompleteableBy to empty)",
    "roleProfilesMapped": "Role Profiles",
    "allRoleProfilesUnlinked": "All role profiles will be unlinked (rolesCompleteableBy set to empty)",
    "mapCustomFields": "Map Custom Fields",
    "customFieldMappingMessage": "The uploaded workflow contains custom fields. Please map them to existing custom fields in your organization:",
    "mapToCustomField": "Map to existing custom field",
    "selectCustomField": "Select custom field",
    "noCustomFieldsTitle": "No custom fields available to map",
    "noCustomFieldsMessage": "The uploaded workflow contains custom fields, but there are no custom fields available in your organization to map them to.",
    "noCustomFieldsHint": "You can either create the required custom fields first, or choose to unlink all custom fields to continue.",
    "requiredCustomFields": "Custom fields required by this workflow:",
    "unlinkAllCustomFields": "Unlink all custom fields (remove custom field configurations)",
    "customFieldsMapped": "Custom Fields",
    "allCustomFieldsUnlinked": "All custom field configurations will be removed from options"
  },
  "pageConfigUpload": {
    "button": "Upload",
    "success": "Page configuration created successfully!",
    "error": "Failed to create page configuration",
    "validationError": "Validation Error",
    "validationMessage": "The uploaded JSON file has the following issues:",
    "invalidJsonFile": "Invalid JSON file. Please check the file format.",
    "missingPageConfiguration": "Missing required field: pageConfiguration",
    "missingName": "Missing required field: name",
    "missingType": "Missing required field: type",
    "missingTabs": "Missing required field: tabs",
    "tabsMustBeArray": "Field \"tabs\" must be an array",
    "tabMissingLabel": "Tab {{index}}: Missing required field \"label\"",
    "tabMissingWidgets": "Tab {{index}}: Missing required field \"widgets\"",
    "tabWidgetsMustBeArray": "Tab {{index}}: Field \"widgets\" must be an array",
    "widgetMissingType": "Tab {{tabIndex}}, Widget {{widgetIndex}}: Missing required field \"type\"",
    "usedIn": "Used in: {{title}}",
    "uploadError": "Upload Error",
    "workflowWarning": "Workflow Warning",
    "confirmTitle": "Upload Page Configuration",
    "confirmMessage": "Are you sure you want to create a new page configuration from this file?",
    "create": "Create Page Configuration",
    "fileName": "File",
    "name": "Name",
    "type": "Type",
    "tabs": "Tabs",
    "mapWorkflows": "Map Workflows",
    "workflowMappingMessage": "The uploaded page configuration contains workflow references. Please map them to existing workflows in your organization:",
    "selectWorkflow": "Select workflow",
    "noWorkflowsTitle": "No workflows available to map",
    "noWorkflowsMessage": "The uploaded page configuration contains workflow references, but there are no workflows available in your organization to map them to.",
    "noWorkflowsHint": "Workflow references will be set to \"default\". You can update them after creating the page configuration.",
    "workflowsInConfig": "Workflows in this configuration:",
    "workflowsMapped": "Workflows Mapped",
    "mapCustomFields": "Map Custom Fields",
    "customFieldMappingMessage": "The uploaded page configuration contains custom fields. Please map them to existing custom fields in your organization:",
    "selectCustomField": "Select custom field",
    "noCustomFieldsTitle": "No custom fields available to map",
    "noCustomFieldsMessage": "The uploaded page configuration contains custom fields, but there are no custom fields available in your organization to map them to.",
    "noCustomFieldsHint": "You can either create the required custom fields first, or choose to unlink all custom fields to continue.",
    "requiredCustomFields": "Custom fields required:",
    "unlinkAllCustomFields": "Unlink all custom fields (remove custom field configurations)",
    "customFieldsMapped": "Custom Fields",
    "allCustomFieldsUnlinked": "All custom field configurations will be removed"
  }
}
